import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { Button, Field } from '@/components/ui';
import { Avatar } from '@/components/avatar';

/**
 * Picks someone from the directory — a search box rather than a full
 * dropdown of everyone, the same reasoning `AddMemberPicker` in Groups
 * already follows (a church of two thousand cannot be a `<select>`, and
 * whoever is searching almost always knows the name).
 */
export function PersonPicker({
  label,
  placeholder,
  exclude = [],
  onPick,
}: {
  readonly label: string;
  readonly placeholder: string;
  readonly exclude?: readonly string[];
  readonly onPick: (personId: string, displayName: string) => void;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['people', 'picker', search],
    queryFn: ({ signal }) => peopleApi.list({ search, limit: 8 }, signal),
    enabled: search.trim().length >= 2,
  });

  const candidates = (query.data?.data ?? []).filter(
    (person) => !exclude.includes(person.id) && !person.archivedAt,
  );

  return (
    <div className="member-picker">
      <Field
        label={label}
        name="person-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={placeholder}
      />

      {search.trim().length >= 2 && candidates.length === 0 && !query.isPending && (
        <p className="field-hint">{t('common.states.noResults')}</p>
      )}

      {candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button
                variant="ghost"
                onClick={() => {
                  onPick(person.id, person.displayName);
                  setSearch('');
                }}
              >
                <Avatar
                  photoFileId={person.photoFileId}
                  displayName={person.displayName}
                  size={24}
                />
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
